import authService from './auth.service';
import type { GroupUser } from '../types/group.types';
import type { Role } from '../types/role.types';
import type { DeactivatePreview } from '../types/user.types';

const API_URL = '/api/v1';

class UsersService {
  async searchUsers(q?: string, skip = 0, limit = 20): Promise<GroupUser[]> {
    const url = new URL(`${API_URL}/users`, window.location.origin);
    if (q && q.trim() !== '') {
      url.searchParams.set('q', q.trim());
    }
    url.searchParams.set('skip', String(skip));
    url.searchParams.set('limit', String(limit));

    const res = await authService.fetchWithAuth(url.pathname + url.search);
    if (!res.ok) {
      throw new Error('Не удалось загрузить пользователей');
    }
    const data = await res.json();
    // Бэкенд мог перейти на пагинацию { items, total, page, page_size }
    if (Array.isArray(data)) return data as GroupUser[];
    if (data && Array.isArray((data as any).items)) return (data as any).items as GroupUser[];
    return [];
  }

  async listUsers(params: { q?: string; page?: number; page_size?: number }): Promise<{ items: GroupUser[]; total: number; page: number; page_size: number; }> {
    const { q, page = 1, page_size = 10 } = params || {};
    const url = new URL(`${API_URL}/users`, window.location.origin);
    if (q && q.trim() !== '') url.searchParams.set('q', q.trim());
    url.searchParams.set('page', String(page));
    url.searchParams.set('page_size', String(page_size));

    const res = await authService.fetchWithAuth(url.pathname + url.search);
    if (!res.ok) {
      const err = await res.json().catch(() => null);
      throw new Error(err?.detail || 'Не удалось загрузить пользователей');
    }
    const data = await res.json();
    const items: GroupUser[] = Array.isArray((data as any)?.items) ? (data as any).items : Array.isArray(data) ? (data as any) : [];
    const total = Number((data as any)?.total ?? (Array.isArray(data) ? (data as any).length : 0));
    const pageVal = Number((data as any)?.page ?? page);
    const pageSizeVal = Number((data as any)?.page_size ?? page_size);
    return { items, total, page: pageVal, page_size: pageSizeVal };
  }

  async createUser(payload: {
    username: string;
    email: string;
    full_name: string;
    password: string;
    is_admin: boolean;
    organization_id?: number | null;
  }): Promise<GroupUser> {
    const res = await authService.fetchWithAuth(`${API_URL}/users`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => null);
      throw new Error(err?.detail || 'Не удалось создать пользователя');
    }
    return (await res.json()) as GroupUser;
  }

  async getUser(id: number): Promise<GroupUser> {
    const res = await authService.fetchWithAuth(`${API_URL}/users/${id}`);
    if (!res.ok) {
      throw new Error('Не удалось загрузить пользователя');
    }
    return (await res.json()) as GroupUser;
  }

  async updateUserPartial(
    id: number,
    patch: Partial<Pick<GroupUser, 'full_name' | 'is_active' | 'is_admin'>> & { password?: string }
  ): Promise<GroupUser> {
    const res = await authService.fetchWithAuth(`${API_URL}/users/${id}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(patch),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => null);
      throw new Error(err?.detail || 'Не удалось обновить пользователя');
    }
    return (await res.json()) as GroupUser;
  }

  async getUserRoles(id: number): Promise<Role[]> {
    const res = await authService.fetchWithAuth(`${API_URL}/users/${id}/roles`);
    if (!res.ok) {
      const err = await res.json().catch(() => null);
      throw new Error(err?.detail || 'Не удалось загрузить роли пользователя');
    }
    const data = await res.json();
    return Array.isArray(data) ? (data as Role[]) : [];
  }

  async getDeactivatePreview(id: number): Promise<DeactivatePreview> {
    const res = await authService.fetchWithAuth(`${API_URL}/users/${id}/deactivate-preview`);
    if (!res.ok) {
      const err = await res.json().catch(() => null);
      throw new Error(err?.detail || 'Не удалось загрузить предпросмотр деактивации');
    }
    return (await res.json()) as DeactivatePreview;
  }

  async deactivateUser(
    id: number,
    reassignToUserId?: number,
    skipEntities?: string[]
  ): Promise<void> {
    const payload: { reassign_to_user_id?: number; skip_entities?: string[] } = {};
    if (reassignToUserId) {
      payload.reassign_to_user_id = reassignToUserId;
    }
    if (skipEntities && skipEntities.length > 0) {
      payload.skip_entities = skipEntities;
    }
    const res = await authService.fetchWithAuth(`${API_URL}/users/${id}/deactivate`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => null);
      throw new Error(err?.detail || 'Не удалось деактивировать пользователя');
    }
  }
}

export default new UsersService();
